/**
 * ArubaWlansPage — fleet-wide live SSID listing.
 *
 * Sourced from /api/aruba/wlans/, which proxies Aruba's
 * /monitoring/v2/networks endpoint — live broadcast view, not the
 * per-group config template. Useful for "is SSID X actually deployed
 * across the fleet?" and "how many clients on guest vs. employee
 * networks?" sanity checks.
 *
 * Read-only; SSID config is managed in Central. Type-bucket filter chips
 * (Employee / Guest / etc.) plus free-text search keep large tenants
 * (test apigw-ca returns 869 SSIDs) usable without scrolling forever.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, Search, Wifi, Lock, Unlock } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import {
  TableShell,
  TableHeadRow,
  TableRow,
  TableHeadCell,
  TableCell,
} from "@/components/ui/table-shell";
import { TableSkeleton } from "@/components/ui/skeleton";
import { ArubaSubNav } from "@/components/app/aruba-sub-nav";
import { NoVendorKeysBanner } from "@/components/app/no-vendor-keys-banner";
import { useArubaWlans } from "@/stores/aruba-wlans";
import { cn } from "@/lib/cn";
import { useUiAutoAnimate } from "@/components/ui/auto-animate";

const COLS = "1.6fr 0.7fr 1fr 0.7fr 0.7fr";
const MAX_ROWS = 200;

function securityBadge(security: string | null | undefined) {
  if (!security)
    return (
      <Badge variant="neutral" size="sm">
        —
      </Badge>
    );
  const open =
    security.toLowerCase().includes("open") ||
    security.toLowerCase() === "none";
  return (
    <Badge variant={open ? "warn" : "neutral"} size="sm">
      {open ? <Unlock size={10} /> : <Lock size={10} />}
      {security}
    </Badge>
  );
}

export function ArubaWlansPage() {
  const animatedRootRef = useUiAutoAnimate();

  const { data, status, error, load } = useArubaWlans();
  useEffect(() => {
    void load();
  }, [load]);

  const [query, setQuery] = useState("");
  const [activeTypes, setActiveTypes] = useState<Set<string>>(new Set());

  // Derive the type chip set from real data. Some tenants have unusual
  // bucket names ("Open", "Voice", custom), so we don't hard-code.
  const types = useMemo(() => {
    const c: Record<string, number> = {};
    for (const w of data) {
      const t = (w.type ?? "other").trim() || "other";
      c[t] = (c[t] ?? 0) + 1;
    }
    return Object.entries(c).sort((a, b) => b[1] - a[1]);
  }, [data]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return data.filter((w) => {
      if (
        activeTypes.size > 0 &&
        !activeTypes.has((w.type ?? "other").trim() || "other")
      ) {
        return false;
      }
      if (!q) return true;
      return (
        (w.essid ?? "").toLowerCase().includes(q) ||
        (w.security ?? "").toLowerCase().includes(q) ||
        (w.encryption ?? "").toLowerCase().includes(q)
      );
    });
  }, [data, query, activeTypes]);

  const visible = filtered.slice(0, MAX_ROWS);
  const overflow = filtered.length - visible.length;

  // Aggregate KPIs — total SSIDs, distinct types, total clients, total APs.
  const stats = useMemo(() => {
    let clients = 0;
    let aps = 0;
    let openSsids = 0;
    for (const w of data) {
      clients += w.client_count ?? 0;
      aps += w.ap_count ?? 0;
      const text = (w.security ?? "").toLowerCase();
      if (text.includes("open") || text === "none" || text === "") openSsids += 1;
    }
    return { clients, aps, openSsids };
  }, [data]);

  function toggleType(t: string) {
    setActiveTypes((prev) => {
      const next = new Set(prev);
      if (next.has(t)) next.delete(t);
      else next.add(t);
      return next;
    });
  }

  return (
    <div ref={animatedRootRef} className="flex flex-col gap-6">
      <div className="flex items-center gap-2 text-xs text-fg-mute">
        <Link to="/" className="text-fg-dim hover:text-fg">
          Operations
        </Link>
        <span className="opacity-40">/</span>
        <Link to="/aruba/keys" className="text-fg-dim hover:text-fg">
          Aruba
        </Link>
        <span className="opacity-40">/</span>
        <span className="text-fg">WLANs</span>
      </div>

      <ArubaSubNav />

      <NoVendorKeysBanner vendor="aruba" />

      <div className="flex items-end justify-between gap-6">
        <div>
          <h1 className="m-0 text-3xl font-extrabold tracking-tight text-aurora">
            WLANs
          </h1>
          <p className="mt-1.5 max-w-[680px] text-sm text-fg-dim">
            SSIDs broadcasting live across your Aruba fleet — Employee, Guest,
            Voice, etc. Sourced from Central's /monitoring/v2/networks endpoint,
            so this reflects actual on-air state rather than the per-group
            config template.
          </p>
        </div>
        <Badge variant="info" size="md">
          {data.length} SSIDs
        </Badge>
      </div>

      {error ? (
        <output
          className="flex items-center gap-2.5 rounded-xl border border-rose/30 bg-rose/10 px-3.5 py-2.5 text-sm text-rose"
        >
          <AlertTriangle size={16} />
          <span>
            <b className="font-semibold">Unable to load WLANs.</b>{" "}
            <span className="text-rose/80">{error}</span>
          </span>
        </output>
      ) : null}

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <KpiTile label="SSIDs" value={data.length} />
        <KpiTile label="Clients" value={stats.clients} />
        <KpiTile label="APs" value={stats.aps} />
        <KpiTile
          label="Open SSIDs"
          value={stats.openSsids}
          tone={stats.openSsids > 0 ? "warn" : undefined}
        />
      </div>

      <Card>
        <CardContent className="flex flex-wrap items-center gap-3">
          <div className="relative max-w-md flex-1">
            <Search
              size={14}
              className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-fg-mute"
            />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Filter by SSID, security, encryption…"
              className="pl-9"
            />
          </div>
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="font-mono text-[10px] uppercase tracking-[0.12em] text-fg-mute">
              type
            </span>
            {types.map(([t, n]) => {
              const on = activeTypes.has(t);
              return (
                <button
                  key={t}
                  type="button"
                  onClick={() => toggleType(t)}
                  className={cn(
                    "inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[11px] font-semibold transition-colors",
                    on
                      ? "border-violet/40 bg-aurora-soft text-violet"
                      : "border-line bg-surface text-fg-mute hover:bg-surface-2 hover:text-fg",
                  )}
                >
                  {t}
                  <span className="ml-0.5 font-mono text-[9.5px] opacity-70">
                    {n}
                  </span>
                </button>
              );
            })}
          </div>
          <span className="ml-auto font-mono text-xs text-fg-mute">
            {filtered.length}/{data.length} match
            {overflow > 0 ? <> · capped to first {MAX_ROWS}</> : null}
          </span>
        </CardContent>
      </Card>

      {status === "loading" && data.length === 0 ? (
        <TableSkeleton columns={COLS} columnCount={5} rows={6} />
      ) : (
        <TableShell>
          <TableHeadRow columns={COLS}>
            <TableHeadCell>SSID</TableHeadCell>
            <TableHeadCell>Type</TableHeadCell>
            <TableHeadCell>Security</TableHeadCell>
            <TableHeadCell className="text-right">Clients</TableHeadCell>
            <TableHeadCell className="text-right">APs</TableHeadCell>
          </TableHeadRow>
          {visible.map((w) => (
            <TableRow key={[w.essid, w.type, w.security].filter(Boolean).join("|") || "unnamed-wlan"} columns={COLS}>
              <TableCell>
                <div className="flex items-center gap-1.5">
                  <Wifi size={11} className="shrink-0 text-violet" />
                  <span className="truncate font-semibold text-fg">
                    {w.essid ?? <span className="text-fg-mute">(unnamed)</span>}
                  </span>
                </div>
              </TableCell>
              <TableCell className="text-xs text-fg-dim">
                {w.type ?? "—"}
              </TableCell>
              <TableCell>{securityBadge(w.security)}</TableCell>
              <TableCell className="text-right font-mono text-xs text-fg">
                {(w.client_count ?? 0).toLocaleString()}
              </TableCell>
              <TableCell className="text-right font-mono text-xs text-fg-dim">
                {(w.ap_count ?? 0).toLocaleString()}
              </TableCell>
            </TableRow>
          ))}
          {visible.length === 0 ? (
            <div className="px-4 py-8 text-center text-xs text-fg-mute">
              {query || activeTypes.size > 0
                ? "No SSIDs match your filter."
                : "No SSIDs in this tenant."}
            </div>
          ) : null}
          {overflow > 0 ? (
            <div className="border-t border-line bg-surface-tint/40 px-4 py-2 text-center text-[11px] text-fg-mute">
              {overflow.toLocaleString()} more match — refine the filter to see
              them.
            </div>
          ) : null}
        </TableShell>
      )}
    </div>
  );
}

function KpiTile({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone?: "warn";
}) {
  return (
    <Card>
      <CardContent className="py-3">
        <div className="font-mono text-[10px] uppercase tracking-[0.12em] text-fg-mute">
          {label}
        </div>
        <div
          className={cn(
            "text-xl font-extrabold",
            tone === "warn" && value > 0 ? "text-peach" : "text-fg",
          )}
        >
          {value.toLocaleString()}
        </div>
      </CardContent>
    </Card>
  );
}
